import {useDispatch, useSelector, useStore} from "react-redux";
import React, {useState} from "react";
import {KonvaEventObject} from "konva/types/Node";
import {Vector2d} from "konva/types/types";
import {topViewWallAreaActions, selectActiveTopViewWallAreaId, selectTopViewWallAreas} from "./topViewWallAreaSlice";
import {setActiveOnTopLayer, lineAngle} from "../../utils";
import {TopViewWallAreaMeasurement, ToolHookResult} from "../../models/editor";
import {selectActiveTool} from "../config/configSlice";
import {EditorTool} from "../../models/enums";
import {CursorStyle} from "../../../../../base-konva/enums";
import {useKeyboardOnEditor} from "../../hooks/useKeyboardOnEditor";
import {useSelectionCleaner} from "../../hooks/select";
import {useDoubleClick} from "../../hooks/useDoubleClick";
import {getId} from "../../../../../../utils";
import {KonvaEventType} from "../../../../../base-konva/types";
import {getPointerPosition, getSnappedPosition} from "../../../../../base-konva/utils";
import {TopViewWallAreaElement} from "./TopViewWallAreaElement";
import {useCursorStyle} from "../../hooks/useCursorStyle";
import {useSnapToPoint} from "../length/snapUtils";
import {selectSnappingState} from "../view/viewSlice";
import {RootState} from "../../editorStore";

export function useTopViewWallAreaTool(isPointerOverLayer: boolean): ToolHookResult {
	const dispatch = useDispatch();
	const [hookRenderId] = useState(getId());
	const activeTool = useSelector(selectActiveTool);
	const measurements = useSelector(selectTopViewWallAreas);
	const activeId = useSelector(selectActiveTopViewWallAreaId);
	const activeMeasurement = measurements.find(m => m.id === activeId);
	const snapping = useSelector(selectSnappingState);
	const [pointerLocation, setPointerLocation] = useState<Vector2d>({x: -50, y: -50});

	const store = useStore<RootState>();
	const {changeCursor} = useCursorStyle();
	const {snapToPoint} = useSnapToPoint();

	useSelectionCleaner(EditorTool.TOP_VIEW_WALL_AREA);

	useKeyboardOnEditor({
		when: activeTool === EditorTool.TOP_VIEW_WALL_AREA,
		keyUpMap: {
			Enter: handleFinishSegment,
			Escape: handleCancel,
		},
	});

	function handleCloseWall() {
		const wallAreaState = store.getState().undoGroup.present.topViewWallArea;
		const measurement = wallAreaState.topViewWallAreas.find(m => m.id === wallAreaState.activeTopViewWallAreaId);
		if (measurement && canCloseWall(measurement)) {
			dispatch(topViewWallAreaActions.closeWall());
		}
	}

	function handleFinishSegment() {
		const wallAreaState = store.getState().undoGroup.present.topViewWallArea;
		const measurement = wallAreaState.topViewWallAreas.find(m => m.id === wallAreaState.activeTopViewWallAreaId);
		if (measurement && canFinishSegment(measurement)) {
			dispatch(topViewWallAreaActions.finishSegment());
		}
	}

	function handleCancel() {
		dispatch(topViewWallAreaActions.cancelActiveSegment());
	}

	function canFinishSegment(measurement: TopViewWallAreaMeasurement): boolean {
		const segment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
		return !!segment && !segment.closed && segment.lines.length >= 1;
	}

	// Closing snaps back to the first point so it needs at least 2 lines (3 points) to form a polygon
	function canCloseWall(measurement: TopViewWallAreaMeasurement): boolean {
		const segment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
		return !!segment && !segment.closed && segment.lines.length >= 2;
	}

	function getActiveSegmentLastPosition(): Vector2d | null {
		if (!activeMeasurement) return null;
		const seg = activeMeasurement.wallSegments.find(s => s.id === activeMeasurement.activeSegmentId);
		return seg?.lastMouseUpPosition ?? null;
	}

	function getActiveSegmentPrevAngle(): number | null {
		if (!activeMeasurement) return null;
		const seg = activeMeasurement.wallSegments.find(s => s.id === activeMeasurement.activeSegmentId);
		if (!seg || seg.lines.length === 0) return null;
		const lastLine = seg.lines[seg.lines.length - 1];
		return lineAngle(lastLine.from.position, lastLine.to.position);
	}

	// Snap to 90° increments relative to baseAngle by rotating the coordinate frame.
	function snapTo90RelativeTo(start: Vector2d, pointer: Vector2d, baseAngle: number): Vector2d {
		const rad = -baseAngle * Math.PI / 180;
		const dx = pointer.x - start.x;
		const dy = pointer.y - start.y;
		const snapped = getSnappedPosition(
			{x: 0, y: 0},
			{x: dx * Math.cos(rad) - dy * Math.sin(rad), y: dx * Math.sin(rad) + dy * Math.cos(rad)},
			90,
		);
		const backRad = baseAngle * Math.PI / 180;
		return {
			x: start.x + snapped.x * Math.cos(backRad) - snapped.y * Math.sin(backRad),
			y: start.y + snapped.x * Math.sin(backRad) + snapped.y * Math.cos(backRad),
		};
	}

	// Resolve final position: point snap wins over angle snap.
	// Pre-computed snap result passed in to avoid calling snapToPoint twice.
	// shiftAngleSnap: true when shift is held — snaps 90° relative to the previous segment direction.
	function resolvePosition(
		rawPosition: Vector2d,
		lastMouseUpPosition: Vector2d | null | undefined,
		pointSnapResult?: {snapTo: Vector2d; isClosing: boolean},
		shiftAngleSnap?: boolean,
		prevAngle?: number | null,
	): Vector2d {
		const hasPointSnap =
			snapping.point &&
			pointSnapResult &&
			(pointSnapResult.snapTo.x !== rawPosition.x || pointSnapResult.snapTo.y !== rawPosition.y);
		if (hasPointSnap) {
			return pointSnapResult!.snapTo;
		}
		if (lastMouseUpPosition) {
			if (shiftAngleSnap) {
				return prevAngle != null
					? snapTo90RelativeTo(lastMouseUpPosition, rawPosition, prevAngle)
					: getSnappedPosition(lastMouseUpPosition, rawPosition, 90);
			}
			if (snapping.angle) {
				return getSnappedPosition(lastMouseUpPosition, rawPosition);
			}
		}
		return rawPosition;
	}

	function onSingleClick(event: KonvaEventObject<KonvaEventType>) {
		if (measurements.length === 0) return;

		const rawPosition = getPointerPosition(event);
		if (!rawPosition) return;

		if (activeMeasurement) {
			const snapResult = snapping.point ? snapToPoint(rawPosition) : undefined;

			if (snapResult?.isClosing) {
				handleCloseWall();
				return;
			}

			const shiftHeld = event.evt.shiftKey;
			const position = resolvePosition(rawPosition, getActiveSegmentLastPosition(), snapResult, shiftHeld, shiftHeld ? getActiveSegmentPrevAngle() : undefined);
			dispatch(topViewWallAreaActions.addPoint(position));
		}
	}

	function onDoubleClick() {
		handleFinishSegment();
	}

	const {onMouseUp} = useDoubleClick({onSingleClick, onDoubleClick});

	function onMouseMove(event: KonvaEventObject<KonvaEventType>) {
		const rawPosition = getPointerPosition(event);
		if (!rawPosition) return;

		changeCursor({event, cursor: CursorStyle.CROSSHAIR});

		const lastPos = getActiveSegmentLastPosition();
		const shiftHeld = event.evt.shiftKey;
		const snapResult = activeMeasurement && snapping.point ? snapToPoint(rawPosition) : undefined;
		const snappedPosition = resolvePosition(rawPosition, lastPos, snapResult, shiftHeld, shiftHeld ? getActiveSegmentPrevAngle() : undefined);
		setPointerLocation(snappedPosition);
	}

	function render() {
		return setActiveOnTopLayer(measurements, activeId).map((measurement: TopViewWallAreaMeasurement) =>
			React.createElement(TopViewWallAreaElement, {
				key: measurement.id,
				measurement,
				pointerLocation: measurement.id === activeId ? pointerLocation : undefined,
				isPointerOverLayer,
			})
		);
	}

	return {
		id: hookRenderId,
		render,
		tool: EditorTool.TOP_VIEW_WALL_AREA,
		callbacks: {
			onMouseUp,
			onMouseMove,
		},
	};
}
